export interface ColumnProps {
  readonly index: number;
  /** Total columns on this board, for "column 3 of 9". */
  readonly cols: number;
  /** Total rows, so "spaces left" is right on any geometry. */
  readonly rows: number;
  readonly playable: boolean;
  readonly full: boolean;
  readonly focused: boolean;
  readonly discsInColumn: number;
  readonly onDrop: (column: number) => void;
  readonly onFocusColumn: (column: number) => void;
  readonly registerRef: (column: number, element: HTMLButtonElement | null) => void;
}

/**
 * The interactive unit of the board.
 *
 * A player picks a column and gravity picks the cell, so the column — not the
 * cell — is the control. The target spans the full board height, which makes the
 * irreversible drop hard to misclick and gives keyboard users a one-dimensional
 * model that needs only left/right and Enter.
 *
 * Full columns stay focusable while being `aria-disabled`, so a keyboard user is
 * never silently skipped past part of the board.
 *
 * The geometry arrives as props rather than as imported constants: a 9x7
 * authored board must announce "column 3 of 9", not "of 7".
 */
export function Column({
  index,
  cols,
  rows,
  playable,
  full,
  focused,
  discsInColumn,
  onDrop,
  onFocusColumn,
  registerRef,
}: ColumnProps) {
  const remaining = rows - discsInColumn;
  const label = full
    ? `Column ${index + 1} of ${cols}, full`
    : `Drop in column ${index + 1} of ${cols}, ${discsInColumn} ${
        discsInColumn === 1 ? 'disc' : 'discs'
      }, ${remaining} ${remaining === 1 ? 'space' : 'spaces'} left`;

  return (
    <button
      type="button"
      ref={(element) => registerRef(index, element)}
      className={`column${playable ? ' column--playable' : ''}`}
      style={{ gridColumn: index + 1 }}
      tabIndex={focused ? 0 : -1}
      aria-disabled={!playable}
      aria-label={label}
      onClick={() => {
        if (playable) onDrop(index);
      }}
      onFocus={() => onFocusColumn(index)}
    >
      <span className="column__hint" aria-hidden="true" />
    </button>
  );
}
